import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, PaletteMode } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

interface ThemeContextType {
  mode: 'light' | 'dark' | 'auto';
  setMode: (mode: 'light' | 'dark' | 'auto') => void;
  resolvedMode: PaletteMode;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  setMode: () => {},
  resolvedMode: 'light',
});

export const useThemeMode = () => useContext(ThemeContext);

const getSystemPreference = (): PaletteMode => {
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

const buildTheme = (mode: PaletteMode) =>
  createTheme({
    palette: {
      mode,
      ...(mode === 'light'
        ? {
            primary: {
              main: '#1F1611',
              light: '#292522',
              dark: '#0F0B08',
              contrastText: '#FFFFFF',
            },
            secondary: {
              main: '#D97706',
              light: '#F59E0B',
              dark: '#92400E',
              contrastText: '#FFFFFF',
            },
            background: {
              default: '#F9FAFB',
              paper: '#FFFFFF',
            },
            text: {
              primary: '#1F1611',
              secondary: '#57534E',
            },
            success: { main: '#059669', light: '#10B981', dark: '#047857' },
            warning: { main: '#D97706', light: '#F59E0B', dark: '#B45309' },
            error: { main: '#DC2626', light: '#EF4444', dark: '#B91C1C' },
          }
        : {
            primary: {
              main: '#E8E0D8',
              light: '#F5F0EB',
              dark: '#C4B8AC',
              contrastText: '#0F0B08',
            },
            secondary: {
              main: '#F59E0B',
              light: '#FBBF24',
              dark: '#D97706',
              contrastText: '#0F0B08',
            },
            background: {
              default: '#121212',
              paper: '#1E1E1E',
            },
            text: {
              primary: '#E8E0D8',
              secondary: '#A8A29E',
            },
            success: { main: '#10B981', light: '#34D399', dark: '#059669' },
            warning: { main: '#F59E0B', light: '#FBBF24', dark: '#D97706' },
            error: { main: '#EF4444', light: '#F87171', dark: '#DC2626' },
            divider: 'rgba(255,255,255,0.12)',
          }),
    },
    typography: {
      fontFamily:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
      h1: { fontWeight: 600, fontSize: '3.5rem', lineHeight: 1.2, letterSpacing: '-0.02em' },
      h2: { fontWeight: 600, fontSize: '2.5rem', lineHeight: 1.3, letterSpacing: '-0.01em' },
      h3: { fontWeight: 600, fontSize: '2rem', lineHeight: 1.3 },
      h4: { fontWeight: 600, fontSize: '1.75rem', lineHeight: 1.4 },
      h5: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.4 },
      h6: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.4 },
      body1: { fontSize: '1rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.6 },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            padding: '10px 24px',
            fontSize: '0.95rem',
            fontWeight: 500,
            boxShadow: 'none',
            '&:hover': { boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' },
          },
          contained: {
            '&:hover': { boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)' },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '16px',
            boxShadow: mode === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
          },
          elevation1: {
            boxShadow: mode === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
          },
          elevation2: {
            boxShadow: mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
          },
          elevation3: {
            boxShadow: mode === 'dark' ? '0 8px 24px rgba(0, 0, 0, 0.5)' : '0 8px 24px rgba(0, 0, 0, 0.12)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '16px',
            boxShadow: mode === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
            ...(mode === 'dark' && { border: '1px solid rgba(255,255,255,0.08)' }),
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
            color: mode === 'dark' ? '#E8E0D8' : '#1F1611',
            boxShadow: mode === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: '8px', fontWeight: 500 },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            ...(mode === 'dark' && { backgroundColor: '#1E1E1E' }),
          },
        },
      },
    },
  });

export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth0();

  // Initialize from localStorage for instant load, then sync from user prefs
  const [mode, setModeState] = useState<'light' | 'dark' | 'auto'>(() => {
    const stored = localStorage.getItem('onsite-theme-mode');
    if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored;
    return 'light';
  });

  const [systemPref, setSystemPref] = useState<PaletteMode>(getSystemPreference);

  // Listen for system preference changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemPref(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Sync from Auth0 user metadata on login
  useEffect(() => {
    if (user?.user_metadata?.preferences?.display?.theme) {
      const userTheme = user.user_metadata.preferences.display.theme;
      if (userTheme === 'light' || userTheme === 'dark' || userTheme === 'auto') {
        setModeState(userTheme);
        localStorage.setItem('onsite-theme-mode', userTheme);
      }
    }
  }, [user]);

  const setMode = (newMode: 'light' | 'dark' | 'auto') => {
    setModeState(newMode);
    localStorage.setItem('onsite-theme-mode', newMode);
  };

  const resolvedMode: PaletteMode = mode === 'auto' ? systemPref : mode;

  const theme = useMemo(() => buildTheme(resolvedMode), [resolvedMode]);

  // Set data-theme attribute for any CSS that needs it
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedMode);
  }, [resolvedMode]);

  return (
    <ThemeContext.Provider value={{ mode, setMode, resolvedMode }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
